import { useRef, useState } from 'react';
import { GifteeRevealStage } from './GifteeRevealStage';
import { useGifteeReveal } from './useGifteeReveal';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { BoxInfo, CardInfo, FullCardInfo } from '@lub0v/santa-utils';
import {
  Badge,
  Button,
  HiddenFace,
  ParticipantFace,
  Stack,
  Typography,
  ChevronRightIcon,
  HintError,
  WarningIcon,
} from '@src/components';
import { cardIsFilled } from '@src/pages/BoxPage/full/cardStatus';
import { PersonCard } from '../PersonCard/PersonCard';

import { boxCardPath, boxWardPath } from '@src/routing/routes';
import { ThemeArtwork } from '@src/theme/Artwork';
import './Exchange.css';

export interface ExchangeHubProps {
  info: BoxInfo;
  card: FullCardInfo | null;
  ward: FullCardInfo | null;
  cards: CardInfo[];
  onChanged: () => Promise<void> | void;
  onRevealOpen: (fadeIn: boolean) => Promise<void>;
}

export function ExchangeHub({
  info,
  card,
  ward,
  cards,
  onChanged,
  onRevealOpen,
}: ExchangeHubProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const revealButtonRef = useRef<HTMLButtonElement>(null);
  const [showReveal, setShowReveal] = useState(false);
  const { busy, suspense, portraits, frame, result, error, celebrating, reveal, cancel } =
    useGifteeReveal({
      boxKey: info.box.key,
      card,
      cards,
      onChanged,
    });
  const own = cards.find((item) => item.isYou);
  const filled = cardIsFilled(card, info.box);
  const hasGiftee = Boolean(info.wardId);
  const giftee = result || ward;
  const revealed = !busy && Boolean(hasGiftee && (result || card?.isWardKnown) && giftee);
  const cardHref = boxCardPath(info.box.key);
  const wardHref = boxWardPath(info.box.key);

  function startReveal() {
    setShowReveal(true);
    void reveal();
  }

  function closeReveal() {
    setShowReveal(false);
    cancel();
    if (busy)
      void Promise.resolve()
        .then(onChanged)
        .catch(() => {});
  }

  function open(href: string) {
    navigate(href);
  }

  return (
    <div className="exchange-hub" data-testid="game-exchange">
      <div className="exchange-hub__grid">
        <PersonCard
          layout="exchange"
          edges={1}
          decoration="forest"
          className="exchange-hub__snow-card"
          testId="game-exchange-card"
          onClick={() => open(cardHref)}
          avatar={
            <ParticipantFace
              avatar={card?.avatar || own?.avatar}
              frameName={card?.frameName ?? own?.frameName}
              picture={card?.picture || own?.picture}
              size="round"
            />
          }
          title={t('game.exchange.myCard')}
          name={card?.username || own?.username || t('game.cardCreate.name.label')}
          status={
            !filled ? (
              <Badge className="exchange-card__status" tone="neutral">
                <WarningIcon />
                {t('game.exchange.cardEmpty')}
              </Badge>
            ) : undefined
          }
          actions={
            <Button
              className="exchange-card__action"
              icon={<ChevronRightIcon />}
              iconPosition="end"
              variant="blue"
              size="large"
              tabIndex={-1}
              testId="game-exchange-open-card"
            >
              {t('game.exchange.openCard')}
            </Button>
          }
        />
        <div className="exchange-hub__connection" aria-hidden="true">
          <span />
          <ThemeArtwork role="gift" className="exchange-hub__gift" />
          <span />
        </div>
        <PersonCard
          layout="exchange"
          edges={2}
          decoration="sparkles"
          className="exchange-hub__snow-card"
          testId="game-exchange-ward"
          avatar={
            revealed && giftee ? (
              <ParticipantFace card={giftee} size="round" />
            ) : (
              <HiddenFace size="round" label={t('box.card.hiddenLabel')} />
            )
          }
          title={t('game.exchange.giftee')}
          name={
            revealed && giftee
              ? giftee.username
              : info.isActiveDraw
                ? t(hasGiftee ? 'game.exchange.gifteeHidden' : 'game.exchange.noGiftee')
                : t('game.exchange.gifteeWaiting')
          }
          status={
            revealed ? undefined : (
              <Typography as="p" role="body/sm" className="exchange-card__hint">
                {t(
                  info.isActiveDraw && !hasGiftee
                    ? info.isAdmin
                      ? 'game.exchange.noGifteeOrganizer'
                      : 'game.exchange.noGifteeParticipant'
                    : 'game.exchange.gifteeHint',
                )}
              </Typography>
            )
          }
          actions={
            info.isActiveDraw && hasGiftee ? (
              <Stack gap={2}>
                <HintError error={error} />
                <Button
                  ref={revealButtonRef}
                  icon={revealed ? <ChevronRightIcon /> : undefined}
                  iconPosition="end"
                  variant={revealed ? 'blue' : 'primary'}
                  size="large"
                  aria-busy={busy || undefined}
                  disabled={busy || (!revealed && (!card || Boolean(info.box.isArchived)))}
                  onClick={() => (revealed ? open(wardHref) : startReveal())}
                  testId={revealed ? 'game-exchange-open-ward' : 'game-exchange-reveal-ward'}
                >
                  {t(
                    busy
                      ? 'game.exchange.revealing'
                      : revealed
                        ? 'game.exchange.openCard'
                        : 'box.card.reveal',
                  )}
                </Button>
              </Stack>
            ) : undefined
          }
        />
      </div>
      {showReveal && !error && (
        <GifteeRevealStage
          returnFocusRef={revealButtonRef}
          portraits={portraits}
          frame={frame}
          result={result}
          busy={busy}
          suspense={suspense}
          celebrating={celebrating}
          onClose={closeReveal}
          onOpen={onRevealOpen}
        />
      )}
    </div>
  );
}
